import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import CreateComment from './CreateComment';
import { getMediaUrl } from '../utils/mediaUrl';
import { videoSources } from '../utils/videoSources';
import AudioPlayer from './AudioPlayer';
import { getPublicWebUrl } from '../config';
import { openExternalUrl } from '../utils/nativeBridge';

const CommentCard = ({
  comment,
  postId,
  postAuthorId,
  onUpdate,
  onDelete,
  level = 0,
  isMobileDetail = false,
  highlightedCommentId = null,
  compact = false,
  onReplyTo = null,
}) => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const toast = useToast();

  // Like state with micro-animation
  const [isLiked, setIsLiked] = useState(Boolean(comment.isLiked));
  const [likesCount, setLikesCount] = useState(Number(comment.likesCount) || 0);
  const [isLiking, setIsLiking] = useState(false);
  const [likedAnim, setLikedAnim] = useState(false);

  // Menu & Edit state
  const [showMenu, setShowMenu] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Thread collapse & Inline Replies state
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [showReplies, setShowReplies] = useState(false);
  const [replies, setReplies] = useState(comment.replies || null);
  const [repliesCount, setRepliesCount] = useState(
    Number(comment.repliesCount) || (comment.replies ? comment.replies.length : 0)
  );
  const [loadingReplies, setLoadingReplies] = useState(false);

  // Sync like and reply count props
  useEffect(() => {
    setIsLiked(Boolean(comment.isLiked));
    setLikesCount(Number(comment.likesCount) || 0);
    if (comment.repliesCount !== undefined) {
      setRepliesCount(Number(comment.repliesCount));
    }
    if (comment.replies) {
      setReplies(comment.replies);
    }
  }, [comment]);

  const handleLike = async (e) => {
    e?.stopPropagation?.();
    if (!currentUser) {
      toast.info('Log in to like comments');
      return;
    }
    if (isLiking) return;

    const nextLiked = !isLiked;
    const nextCount = nextLiked ? likesCount + 1 : Math.max(0, likesCount - 1);

    // Optimistic UI update
    setIsLiked(nextLiked);
    setLikesCount(nextCount);
    if (nextLiked) {
      setLikedAnim(true);
      setTimeout(() => setLikedAnim(false), 400);
    }

    setIsLiking(true);
    try {
      await axios.post(`/api/posts/${comment.id}/like`);
    } catch (error) {
      console.error('Like error:', error);
      // Revert on failure
      setIsLiked(!nextLiked);
      setLikesCount(likesCount);
      toast.error('Failed to update like');
    } finally {
      setIsLiking(false);
    }
  };

  const toggleReplies = async (e) => {
    e?.stopPropagation?.();
    if (showReplies) {
      setShowReplies(false);
      return;
    }

    if (replies !== null) {
      setShowReplies(true);
      return;
    }

    setLoadingReplies(true);
    try {
      const response = await axios.get(`/api/posts/${postId}/comment/${comment.id}`);
      setReplies(response.data.comment?.replies || []);
      setShowReplies(true);
    } catch (err) {
      console.error('Fetch replies error:', err);
      toast.error('Failed to load replies');
    } finally {
      setLoadingReplies(false);
    }
  };

  const handleReplyCreated = (newReply) => {
    setShowReplyForm(false);
    setRepliesCount((prev) => prev + 1);
    if (replies !== null && newReply) {
      setReplies((prev) => [...(prev || []), newReply]);
      setShowReplies(true);
    } else {
      // Fetch fresh replies
      axios.get(`/api/posts/${postId}/comment/${comment.id}`).then((res) => {
        setReplies(res.data.comment?.replies || []);
        setShowReplies(true);
      }).catch(() => {});
    }
    if (onUpdate) onUpdate();
  };

  const handleReplyUpdated = () => {
    if (postId && comment.id) {
      axios.get(`/api/posts/${postId}/comment/${comment.id}`).then((res) => {
        setReplies(res.data.comment?.replies || []);
        setRepliesCount(res.data.comment?.replies?.length ?? repliesCount);
      }).catch(() => {});
    }
    if (onUpdate) onUpdate();
  };

  const handleReplyDeleted = (replyId) => {
    setReplies((prev) => (prev ? prev.filter((r) => r.id !== replyId) : []));
    setRepliesCount((prev) => Math.max(0, prev - 1));
    if (onUpdate) onUpdate();
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this comment?')) {
      setShowMenu(false);
      return;
    }

    setIsDeleting(true);
    setShowMenu(false);
    try {
      await axios.delete(`/api/posts/${comment.id}`);
      toast.success('Comment deleted');
      if (onDelete) onDelete(comment.id);
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.response?.data?.error || 'Failed to delete comment');
      setIsDeleting(false);
    }
  };

  const startEdit = () => {
    setShowMenu(false);
    setEditContent(comment.content || '');
    setIsEditing(true);
  };

  const handleSaveEdit = async (e) => {
    e?.stopPropagation?.();
    const trimmed = editContent.trim();
    if (trimmed === (comment.content || '').trim()) {
      setIsEditing(false);
      return;
    }
    if (trimmed.length > 280) {
      toast.error('Comment must be 280 characters or less');
      return;
    }
    setIsSaving(true);
    try {
      await axios.put(`/api/posts/${comment.id}`, { content: trimmed });
      toast.success('Comment updated');
      comment.content = trimmed;
      comment.editedAt = new Date().toISOString();
      if (onUpdate) onUpdate();
      setIsEditing(false);
    } catch (err) {
      console.error('Edit comment error:', err);
      toast.error(err.response?.data?.error || 'Failed to update comment');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCopyLink = (e) => {
    e?.stopPropagation?.();
    setShowMenu(false);
    const url = getPublicWebUrl(`/post/${postId}/comment/${comment.id}`);
    navigator.clipboard.writeText(url);
    toast.success('Comment link copied');
  };

  const getTimeAgo = (date) => {
    if (!date) return '';
    const now = new Date();
    const commentDate = new Date(date);
    const seconds = Math.floor((now - commentDate) / 1000);

    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d`;
    return commentDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  const renderFormattedContent = (text) => {
    if (!text) return null;
    const parts = text.split(/(@[a-zA-Z0-9_]+|https?:\/\/[^\s]+)/g);
    return parts.map((part, index) => {
      if (part.startsWith('@')) {
        const handle = part.slice(1);
        return (
          <Link
            key={index}
            to={`/profile/${handle}`}
            onClick={(e) => e.stopPropagation()}
            className="text-sky-600 dark:text-sky-400 font-medium hover:underline inline-block"
            dir="ltr"
          >
            <bdi dir="ltr">{part}</bdi>
          </Link>
        );
      }
      if (part.startsWith('http://') || part.startsWith('https://')) {
        return (
          <a
            key={index}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              openExternalUrl(part);
            }}
            className="text-sky-600 dark:text-sky-400 hover:underline break-all"
          >
            {part}
          </a>
        );
      }
      return part;
    });
  };

  const isOwnComment = currentUser?.id === comment.userId;
  const isPostAuthor = postAuthorId && String(comment.userId) === String(postAuthorId);
  const isHighlighted = highlightedCommentId === comment.id;
  const locationMedia = comment.media?.find((m) => m.type === 'location');
  const mediaWithoutLocation = locationMedia ? comment.media?.filter((m) => m.type !== 'location') : comment.media;

  // Collapsed compact row
  if (isCollapsed) {
    return (
      <div
        id={`comment-${comment.id}`}
        onClick={() => setIsCollapsed(false)}
        className="py-1.5 px-3 my-1 rounded-xl bg-zinc-50 dark:bg-zinc-900/50 hover:bg-zinc-100 dark:hover:bg-zinc-800/60 border border-zinc-200/60 dark:border-zinc-800/60 flex items-center justify-between text-xs text-zinc-500 dark:text-zinc-400 cursor-pointer transition-colors"
      >
        <div className="flex items-center gap-2 truncate">
          <i className="fas fa-plus text-[10px] text-zinc-400" />
          <span className="font-medium text-zinc-700 dark:text-zinc-300">
            <bdi dir="ltr">@{comment.author?.handle}</bdi>
          </span>
          <span>·</span>
          <span>{getTimeAgo(comment.createdAt)}</span>
          {repliesCount > 0 && (
            <span className="text-[11px] text-zinc-400">
              ({repliesCount} {repliesCount === 1 ? 'reply' : 'replies'} collapsed)
            </span>
          )}
        </div>
        <span className="text-[11px] text-sky-600 dark:text-sky-400 font-medium">Expand</span>
      </div>
    );
  }

  return (
    <div
      id={`comment-${comment.id}`}
      className={`group relative transition-all duration-200 ${
        isHighlighted
          ? 'bg-sky-50/70 dark:bg-sky-950/20 rounded-2xl p-2.5 -m-2.5 ring-1 ring-sky-200 dark:ring-sky-900'
          : ''
      } ${compact ? 'py-1.5' : 'py-2.5'}`}
    >
      <div className="flex gap-2.5 sm:gap-3">
        {/* Left Column: Avatar & Continuous Thread Connector Line */}
        <div className="flex flex-col items-center flex-shrink-0">
          <Link
            to={`/profile/${comment.author?.handle}`}
            onClick={(e) => e.stopPropagation()}
            className="relative z-10 transition-transform hover:scale-105 active:scale-95"
          >
            {comment.author?.profilePicture ? (
              <img
                src={getMediaUrl(comment.author.profilePicture)}
                alt={comment.author.username || 'User'}
                className={`rounded-full object-cover ring-1 ring-zinc-200 dark:ring-zinc-800 ${
                  compact ? 'w-7 h-7' : 'w-8 h-8 sm:w-9 sm:h-9'
                }`}
              />
            ) : (
              <div
                className={`rounded-full bg-zinc-200 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300 flex items-center justify-center font-semibold ${
                  compact ? 'w-7 h-7 text-xs' : 'w-8 h-8 sm:w-9 sm:h-9 text-xs sm:text-sm'
                }`}
              >
                {(comment.author?.username || comment.author?.handle || 'U').charAt(0).toUpperCase()}
              </div>
            )}
          </Link>

          {/* Thread continuity line when replies are open */}
          {showReplies && replies && replies.length > 0 && (
            <div
              onClick={() => setIsCollapsed(true)}
              title="Click to collapse thread"
              className="w-0.5 flex-1 bg-zinc-200 dark:bg-zinc-800/80 hover:bg-sky-400 dark:hover:bg-sky-500 cursor-pointer mt-1.5 rounded-full transition-colors"
            />
          )}
        </div>

        {/* Right Column: Comment Content & Actions */}
        <div className="flex-1 min-w-0">
          {/* Header Row */}
          <div className="flex items-center justify-between gap-1 leading-tight mb-1">
            <div className="flex items-center gap-1.5 flex-wrap min-w-0">
              <Link
                to={`/profile/${comment.author?.handle}`}
                onClick={(e) => e.stopPropagation()}
                className="font-bold text-zinc-900 dark:text-zinc-100 hover:underline text-xs sm:text-sm truncate"
              >
                <bdi>{comment.author?.username || comment.author?.handle}</bdi>
              </Link>

              {comment.author?.isVerified && (
                <i className="fas fa-check-circle text-sky-500 text-[11px] flex-shrink-0" />
              )}

              <span className="text-zinc-400 dark:text-zinc-500 text-xs truncate">
                <bdi dir="ltr">@{comment.author?.handle}</bdi>
              </span>

              {/* Author / OP Badge */}
              {isPostAuthor && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-sky-500/10 text-sky-600 dark:text-sky-400 border border-sky-500/20 tracking-tight">
                  <i className="fas fa-feather text-[8px]" />
                  Author
                </span>
              )}

              <span className="text-zinc-300 dark:text-zinc-700 text-xs">·</span>

              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/post/${postId}/comment/${comment.id}`);
                }}
                className="text-zinc-400 dark:text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300 text-xs hover:underline flex-shrink-0"
                title="View comment permalink"
              >
                {getTimeAgo(comment.createdAt)}
              </button>

              {/* Location pin in header if attached */}
              {locationMedia && (
                <a
                  href={locationMedia.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    openExternalUrl(locationMedia.url);
                  }}
                  className="inline-flex items-center gap-1 text-[11px] text-zinc-400 hover:text-rose-500 transition-colors ml-1"
                >
                  <i className="fas fa-location-dot text-[10px] text-rose-500" />
                  <span>Location</span>
                </a>
              )}
            </div>

            {/* Top Right Options Menu & Thread Collapse */}
            <div className="flex items-center gap-1">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsCollapsed(true);
                }}
                className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 p-1 rounded-full hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors opacity-0 group-hover:opacity-100"
                title="Collapse thread"
              >
                <i className="fas fa-minus text-[10px]" />
              </button>

              <div className="relative">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowMenu(!showMenu);
                  }}
                  className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 p-1 rounded-full hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
                  aria-label="Comment options"
                >
                  <i className="fas fa-ellipsis text-xs" />
                </button>

                {showMenu && (
                  <>
                    <div className="fixed inset-0 z-20" onClick={() => setShowMenu(false)} />
                    <div className="absolute right-0 top-7 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-2xl shadow-xl py-1 z-30 min-w-[140px] overflow-hidden text-xs animate-in fade-in duration-150">
                      <button
                        onClick={handleCopyLink}
                        className="w-full text-left px-3.5 py-2 text-zinc-700 dark:text-zinc-200 hover:bg-zinc-50 dark:hover:bg-zinc-800/70 flex items-center gap-2.5 transition-colors"
                      >
                        <i className="fas fa-link text-zinc-400 text-xs" />
                        <span>Copy link</span>
                      </button>

                      {isOwnComment && (
                        <button
                          onClick={startEdit}
                          className="w-full text-left px-3.5 py-2 text-zinc-700 dark:text-zinc-200 hover:bg-zinc-50 dark:hover:bg-zinc-800/70 flex items-center gap-2.5 transition-colors"
                        >
                          <i className="fas fa-pen text-zinc-400 text-xs" />
                          <span>Edit</span>
                        </button>
                      )}

                      {isOwnComment && (
                        <button
                          onClick={handleDelete}
                          disabled={isDeleting}
                          className="w-full text-left px-3.5 py-2 text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/30 flex items-center gap-2.5 transition-colors disabled:opacity-50"
                        >
                          <i className="fas fa-trash-can text-xs" />
                          <span>{isDeleting ? 'Deleting...' : 'Delete'}</span>
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Replying-to context banner */}
          {comment.parentAuthor && (
            <p className="text-[11px] text-zinc-400 dark:text-zinc-500 mb-1 leading-snug">
              Replying to{' '}
              <Link
                to={`/profile/${comment.parentAuthor.handle}`}
                onClick={(e) => e.stopPropagation()}
                className="text-sky-600 dark:text-sky-400 hover:underline font-medium"
              >
                <bdi dir="ltr">@{comment.parentAuthor.handle}</bdi>
              </Link>
            </p>
          )}

          {/* Content / Edit Form */}
          {isEditing ? (
            <div className="mt-1 mb-2 space-y-2" onClick={(e) => e.stopPropagation()}>
              <textarea
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                maxLength={280}
                className="w-full rounded-2xl border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100 p-2.5 text-xs sm:text-sm focus:ring-2 focus:ring-sky-500/30 focus:border-sky-500 outline-none resize-none"
                rows={3}
                autoFocus
              />
              <div className="flex items-center gap-2">
                <button
                  onClick={handleSaveEdit}
                  disabled={isSaving || !editContent.trim()}
                  className="px-3.5 py-1 text-xs font-semibold text-white bg-zinc-900 dark:bg-white dark:text-zinc-900 hover:opacity-90 disabled:opacity-50 rounded-full transition-opacity shadow-sm"
                >
                  {isSaving ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={() => setIsEditing(false)}
                  disabled={isSaving}
                  className="px-3.5 py-1 text-xs font-semibold text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-full transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div
              className={`text-zinc-900 dark:text-zinc-100 text-[14.5px] sm:text-[15.5px] leading-relaxed break-words whitespace-pre-wrap select-text mb-1.5 ${
                onReplyTo ? 'cursor-pointer' : ''
              }`}
              onClick={(e) => {
                const selection = window.getSelection();
                if (selection && selection.toString().length > 0) return;
                if (onReplyTo) {
                  onReplyTo(comment);
                }
              }}
            >
              {renderFormattedContent(comment.content)}
              {comment.editedAt && (
                <span className="ml-1.5 text-zinc-400 dark:text-zinc-500 text-[11px] font-normal">
                  (edited)
                </span>
              )}
            </div>
          )}

          {/* Attachments preview */}
          {mediaWithoutLocation && mediaWithoutLocation.length > 0 && (
            <div className="mb-2 space-y-1.5">
              <div
                className={
                  mediaWithoutLocation.filter((m) => m.type === 'image' || m.type === 'video').length <= 1
                    ? ''
                    : 'grid grid-cols-2 gap-2'
                }
              >
                {mediaWithoutLocation.map((item, idx) => {
                  if (item.type === 'audio') {
                    return (
                      <div key={idx} className="w-full">
                        <AudioPlayer src={item.url} />
                      </div>
                    );
                  }

                  if (item.type === 'text') {
                    return (
                      <a
                        key={idx}
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          openExternalUrl(item.url);
                        }}
                        className="inline-flex items-center gap-2 px-3 py-1.5 bg-zinc-100 dark:bg-zinc-800/80 rounded-xl border border-zinc-200 dark:border-zinc-700 text-xs text-zinc-700 dark:text-zinc-200 hover:bg-zinc-200 dark:hover:bg-zinc-800 transition-colors"
                      >
                        <i className="fas fa-file-lines text-zinc-500 text-sm" />
                        <span className="truncate max-w-[160px]">Text attachment</span>
                      </a>
                    );
                  }

                  return (
                    <div
                      key={idx}
                      className="rounded-2xl overflow-hidden ring-1 ring-zinc-200/80 dark:ring-zinc-800 bg-black/5 dark:bg-white/5"
                    >
                      {item.type === 'image' ? (
                        <img
                          src={getMediaUrl(item.url)}
                          alt={`Media ${idx + 1}`}
                          className={`w-full object-cover ${
                            mediaWithoutLocation.filter((m) => m.type === 'image' || m.type === 'video').length === 1
                              ? 'max-h-80'
                              : 'h-32'
                          }`}
                        />
                      ) : (
                        <video
                          key={item.url}
                          controls
                          onClick={(e) => e.stopPropagation()}
                          className={`w-full object-cover ${
                            mediaWithoutLocation.filter((m) => m.type === 'image' || m.type === 'video').length === 1
                              ? 'max-h-80'
                              : 'h-32'
                          }`}
                        >
                          {videoSources(item.url)}
                        </video>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Actions Row */}
          <div className="flex items-center gap-6 text-zinc-500 dark:text-zinc-400 pt-1">
            {/* Heart reaction */}
            <button
              type="button"
              onClick={handleLike}
              disabled={isLiking}
              className={`flex items-center gap-1.5 transition-colors group/like py-0.5 ${
                isLiked ? 'text-rose-500' : 'hover:text-rose-500'
              }`}
            >
              <i
                className={`${isLiked ? 'fas fa-heart text-rose-500' : 'far fa-heart'} text-[16px] sm:text-[17px] ${
                  likedAnim ? 'animate-heart-pop' : 'group-hover/like:scale-110'
                } transition-transform`}
              />
              <span className={`font-semibold text-xs sm:text-[13px] ${isLiked ? 'text-rose-500' : ''}`}>
                {likesCount > 0 ? likesCount : ''}
              </span>
            </button>

            {/* Quick Reply toggle / Sticky Composer trigger */}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                if (!currentUser) {
                  toast.info('Log in to reply');
                  return;
                }
                if (onReplyTo) {
                  onReplyTo(comment);
                } else {
                  setShowReplyForm(!showReplyForm);
                }
              }}
              className="flex items-center gap-1.5 hover:text-sky-500 transition-colors py-0.5 active:opacity-60"
            >
              <i className="far fa-comment text-[15px] sm:text-[16px]" />
              <span className="font-medium text-xs sm:text-[13px]">Reply</span>
            </button>

            {/* Share / Copy link */}
            <button
              type="button"
              onClick={handleCopyLink}
              className="flex items-center gap-1.5 hover:text-zinc-700 dark:hover:text-zinc-300 transition-colors py-0.5"
              title="Copy comment link"
            >
              <i className="far fa-share-from-square text-[15px] sm:text-[16px]" />
            </button>
          </div>

          {/* Inline Reply Composer Form */}
          {showReplyForm && (
            <div className="mt-3 mb-2 animate-in fade-in duration-200" onClick={(e) => e.stopPropagation()}>
              <div className="bg-zinc-50/80 dark:bg-zinc-900/40 rounded-2xl p-3 border border-zinc-200/80 dark:border-zinc-800">
                <CreateComment
                  postId={postId}
                  parentCommentId={comment.id}
                  replyToUser={comment.author}
                  onCommentCreated={handleReplyCreated}
                  onCancel={() => setShowReplyForm(false)}
                  compact={true}
                />
              </div>
            </div>
          )}

          {/* "View N replies" Accordion Button */}
          {repliesCount > 0 && (
            <div className="mt-1.5">
              <button
                type="button"
                onClick={toggleReplies}
                className="inline-flex items-center gap-2 text-xs font-semibold text-sky-600 dark:text-sky-400 hover:text-sky-700 dark:hover:text-sky-300 py-1 transition-colors group/replies"
              >
                <div className="w-5 h-px bg-zinc-300 dark:bg-zinc-700 group-hover/replies:bg-sky-500 transition-colors" />
                {loadingReplies ? (
                  <span className="flex items-center gap-1.5 text-zinc-400 text-xs">
                    <div className="w-3 h-3 border-2 border-zinc-400 border-t-transparent rounded-full animate-spin" />
                    Loading replies...
                  </span>
                ) : showReplies ? (
                  <span>Hide {repliesCount === 1 ? 'reply' : `${repliesCount} replies`}</span>
                ) : (
                  <span>
                    View {repliesCount === 1 ? '1 reply' : `${repliesCount} replies`}
                  </span>
                )}
              </button>
            </div>
          )}

          {/* Nested Replies List */}
          {showReplies && replies && replies.length > 0 && (
            <div className="mt-2 pl-3 sm:pl-4 border-l-2 border-zinc-200/80 dark:border-zinc-800/80 space-y-1">
              {replies.map((reply) => (
                <CommentCard
                  key={reply.id}
                  comment={reply}
                  postId={postId}
                  postAuthorId={postAuthorId}
                  onUpdate={handleReplyUpdated}
                  onDelete={handleReplyDeleted}
                  level={level + 1}
                  isMobileDetail={isMobileDetail}
                  compact={true}
                  onReplyTo={onReplyTo}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CommentCard;
